/**
 * NAD FAQs Section
 *
 * Handles FAQ section-level animations and delegates accordion functionality to accordion-js.
 * Now uses the simplified AccordionAnimationManager.
 */

import Accordion from 'accordion-js';

class NADFaqsSection {
  private section: HTMLElement | null;
  private accordionInstance: any | null = null;

  constructor(section: HTMLElement) {
    this.section = section;
    this.init();
  }

  private init(): void {
    this.initializeAccordion();
    this.setupScrollAnimation();
  }

  /**
   * Initialize accordion-js (let Accordion handle all accordion logic)
   */
  private initializeAccordion(): void {
    if (!this.section) {
      console.log("🔧 NADFaqsSection: Section not found");
      return;
    }

    const accordionContainer = this.section.querySelector(
      ".accordion-container"
    );
    if (!accordionContainer) return;

    try {
      console.log("🔧 NADFaqsSection: Initializing accordion", accordionContainer);
      this.accordionInstance = new Accordion(
        accordionContainer as HTMLElement,
        {
          duration: 300,
          showMultiple: false,
          openOnInit: [0], // Open first item by default
          elementClass: 'ac',
          triggerClass: 'ac-trigger',
          panelClass: 'ac-panel',
          activeClass: 'is-active'
        }
      );

      // Store instance for cleanup
      (this.section as any).accordion = this.accordionInstance;
      this.section.setAttribute("data-initialized", "true");

      console.log("🔧 NADFaqsSection: Accordion initialized successfully");
    } catch (error) {
      console.error(
        "❌ NADFaqsSection: Failed to initialize accordion:",
        error
      );
    }
  }

  /**
   * Setup scroll animation using Intersection Observer
   */
  private setupScrollAnimation(): void {
    if (!this.section) return;

    const accordionItems = this.section.querySelectorAll('.ac');
    if (accordionItems.length === 0) return;

    // Create intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add animation class when element becomes visible
            entry.target.classList.add('animate-in');
            // Stop observing this element once it's animated
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        rootMargin: '0px 0px -50px 0px' // Start animation slightly before element is fully visible
      }
    );

    // Observe all accordion items
    accordionItems.forEach((item) => {
      observer.observe(item);
    });

    // Store observer for cleanup
    (this.section as any).scrollObserver = observer;
  }

  /**
   * Cleanup method
   */
  public destroy(): void {
    if (
      this.accordionInstance &&
      typeof this.accordionInstance.destroy === "function"
    ) {
      this.accordionInstance.destroy();
      this.accordionInstance = null;
    }

    // Cleanup intersection observer
    if (this.section && (this.section as any).scrollObserver) {
      (this.section as any).scrollObserver.disconnect();
      delete (this.section as any).scrollObserver;
    }
  }

  // Static method to initialize all sections
  public static initializeAll(): void {
    const sections =
      document.querySelectorAll<HTMLElement>(".nad-faqs-section");

    sections.forEach((section) => {
      if (!(section as any)._nadFaqsInstance) {
        (section as any)._nadFaqsInstance = new NADFaqsSection(section);
      }
    });
  }

  // Static method to destroy a section instance
  public static destroySection(section: HTMLElement): void {
    const instance = (section as any)._nadFaqsInstance;
    if (instance && typeof instance.destroy === "function") {
      instance.destroy();
      delete (section as any)._nadFaqsInstance;
    }
  }
}

export { NADFaqsSection };
